import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, MessageCircle, MapPin, Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { COMPANY, SERVICE_AREA } from "@/lib/data";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Kontakt() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    privacy_accepted: false,
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.privacy_accepted) {
      toast.error("Bitte stimmen Sie der Datenschutzerklärung zu.");
      return;
    }
    if (form.name.trim().length < 2 || form.message.trim().length < 5) {
      toast.error("Bitte füllen Sie Name und Nachricht vollständig aus.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Anfrage gesendet — wir melden uns innerhalb von 24 Stunden.");
      setForm({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
        privacy_accepted: false,
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.";
      toast.error(typeof msg === "string" ? msg : "Bitte prüfen Sie Ihre Eingaben.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="kontakt-page">
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 bg-messinger-bg overflow-hidden border-b border-white/5">
        <div className="absolute inset-x-0 top-0 h-[50%] bg-gradient-to-b from-messinger-orange/10 to-transparent pointer-events-none" />
        <div className="container-x relative">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-12 bg-messinger-orange" />
            <span className="text-[11px] tracking-[0.28em] uppercase font-semibold text-messinger-orange">
              Kontakt
            </span>
          </div>
          <h1 className="font-heading font-semibold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl text-balance">
            Schreiben Sie uns. <br />
            <span className="text-messinger-orange">Wir melden uns persönlich.</span>
          </h1>
          <p className="mt-6 text-white/65 text-lg max-w-2xl">
            „Wir sind erst zufrieden, wenn Sie es sind!“ — Anfrage per
            Formular, WhatsApp oder Telefon. Innerhalb von 24 Stunden erhalten
            Sie eine Rückmeldung.
          </p>
        </div>
      </section>

      <section className="bg-messinger-surface py-20 lg:py-28 relative">
        <div className="container-x grid lg:grid-cols-12 gap-10 lg:gap-12 relative">
          {/* FORM */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <div className="surface-card rounded-2xl p-8 lg:p-10">
              {sent ? (
                <div
                  data-testid="contact-success"
                  className="flex flex-col items-start gap-4 py-12"
                >
                  <CheckCircle2 size={48} className="text-messinger-orange" />
                  <h3 className="font-heading font-semibold text-white text-2xl">
                    Vielen Dank für Ihre Nachricht.
                  </h3>
                  <p className="text-white/70 max-w-lg">
                    Ihre Anfrage ist bei uns eingegangen. Sie hören innerhalb
                    von 24 Stunden persönlich von Mike Messinger.
                  </p>
                  <button
                    onClick={() => setSent(false)}
                    data-testid="contact-new-message"
                    className="mt-4 inline-flex items-center gap-2 text-sm font-semibold tracking-[0.14em] uppercase text-white hover:text-messinger-orange transition-colors"
                  >
                    Weitere Nachricht senden <ArrowRight size={16} />
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={onSubmit}
                  data-testid="contact-form"
                  className="grid grid-cols-1 sm:grid-cols-2 gap-5"
                >
                  <div className="sm:col-span-2">
                    <Label htmlFor="name" className="text-xs uppercase tracking-[0.18em] text-white/70">
                      Name *
                    </Label>
                    <Input
                      id="name"
                      data-testid="contact-input-name"
                      value={form.name}
                      onChange={(e) => onChange("name", e.target.value)}
                      placeholder="Ihr Name"
                      className="mt-2 h-12 rounded-xl bg-white/[0.04] border-white/10 text-white placeholder:text-white/40 focus-visible:ring-messinger-orange focus-visible:border-messinger-orange/50"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-xs uppercase tracking-[0.18em] text-white/70">
                      E-Mail *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="contact-input-email"
                      value={form.email}
                      onChange={(e) => onChange("email", e.target.value)}
                      placeholder="name@beispiel.de"
                      className="mt-2 h-12 rounded-xl bg-white/[0.04] border-white/10 text-white placeholder:text-white/40 focus-visible:ring-messinger-orange focus-visible:border-messinger-orange/50"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-xs uppercase tracking-[0.18em] text-white/70">
                      Telefon
                    </Label>
                    <Input
                      id="phone"
                      data-testid="contact-input-phone"
                      value={form.phone}
                      onChange={(e) => onChange("phone", e.target.value)}
                      placeholder="+49 …"
                      className="mt-2 h-12 rounded-xl bg-white/[0.04] border-white/10 text-white placeholder:text-white/40 focus-visible:ring-messinger-orange focus-visible:border-messinger-orange/50"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="subject" className="text-xs uppercase tracking-[0.18em] text-white/70">
                      Betreff
                    </Label>
                    <Input
                      id="subject"
                      data-testid="contact-input-subject"
                      value={form.subject}
                      onChange={(e) => onChange("subject", e.target.value)}
                      placeholder="z. B. Unfallgutachten, Oldtimer-Bewertung"
                      className="mt-2 h-12 rounded-xl bg-white/[0.04] border-white/10 text-white placeholder:text-white/40 focus-visible:ring-messinger-orange focus-visible:border-messinger-orange/50"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="message" className="text-xs uppercase tracking-[0.18em] text-white/70">
                      Nachricht *
                    </Label>
                    <Textarea
                      id="message"
                      data-testid="contact-input-message"
                      value={form.message}
                      onChange={(e) => onChange("message", e.target.value)}
                      placeholder="Beschreiben Sie Ihr Anliegen kurz — wir melden uns persönlich."
                      rows={6}
                      className="mt-2 rounded-2xl bg-white/[0.04] border-white/10 text-white placeholder:text-white/40 focus-visible:ring-messinger-orange focus-visible:border-messinger-orange/50"
                      required
                    />
                  </div>
                  <div className="sm:col-span-2 flex items-start gap-3">
                    <Checkbox
                      id="privacy"
                      data-testid="contact-input-privacy"
                      checked={form.privacy_accepted}
                      onCheckedChange={(v) => onChange("privacy_accepted", Boolean(v))}
                      className="mt-1 border-white/30 data-[state=checked]:bg-messinger-orange data-[state=checked]:border-messinger-orange"
                    />
                    <Label
                      htmlFor="privacy"
                      className="text-sm text-white/70 leading-relaxed font-normal"
                    >
                      Ich habe die Datenschutzerklärung gelesen und stimme zu,
                      dass meine Angaben zur Bearbeitung der Anfrage verwendet
                      werden. *
                    </Label>
                  </div>
                  <div className="sm:col-span-2 pt-2">
                    <button
                      type="submit"
                      data-testid="contact-submit"
                      disabled={loading}
                      className="cta-orange group inline-flex items-center justify-center gap-2 px-8 py-4 bg-messinger-orange text-white font-semibold tracking-tight rounded-full text-sm transition-colors disabled:opacity-70"
                    >
                      {loading ? "Wird gesendet…" : "Anfrage senden"}
                      <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                    </button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>

          {/* SIDEBAR */}
          <motion.aside
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-5 space-y-5"
          >
            <div className="surface-card text-white rounded-2xl p-8 relative overflow-hidden">
              <div className="relative">
                <span className="label-eyebrow">Direkt erreichbar</span>
                <h3 className="mt-3 font-heading font-semibold text-2xl">
                  Wir freuen uns auf Ihre Nachricht.
                </h3>
                <div className="mt-6 space-y-4">
                  <a
                    href={COMPANY.phoneHref}
                    data-testid="kontakt-phone"
                    className="flex items-center justify-between p-4 border border-white/15 rounded-xl hover:border-messinger-orange hover:text-messinger-orange transition-colors"
                  >
                    <span className="flex items-center gap-3">
                      <Phone size={16} className="text-messinger-orange" />
                      <span className="text-sm">{COMPANY.phone}</span>
                    </span>
                    <ArrowRight size={16} />
                  </a>
                  <a
                    href={COMPANY.whatsappHref}
                    data-testid="kontakt-whatsapp"
                    className="flex items-center justify-between p-4 border border-white/15 rounded-xl hover:border-messinger-orange hover:text-messinger-orange transition-colors"
                  >
                    <span className="flex items-center gap-3">
                      <MessageCircle size={16} className="text-messinger-orange" />
                      <span className="text-sm">WhatsApp · {COMPANY.whatsapp}</span>
                    </span>
                    <ArrowRight size={16} />
                  </a>
                  <a
                    href={COMPANY.emailHref}
                    data-testid="kontakt-email"
                    className="flex items-center justify-between p-4 border border-white/15 rounded-xl hover:border-messinger-orange hover:text-messinger-orange transition-colors"
                  >
                    <span className="flex items-center gap-3">
                      <Mail size={16} className="text-messinger-orange" />
                      <span className="text-sm">{COMPANY.email}</span>
                    </span>
                    <ArrowRight size={16} />
                  </a>
                </div>
              </div>
            </div>

            <div className="surface-card rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-4">
                <MapPin size={16} className="text-messinger-orange" />
                <span className="label-eyebrow">Büro</span>
              </div>
              <div className="font-heading font-semibold text-white text-lg">
                {COMPANY.name}
              </div>
              <div className="mt-2 text-sm text-white/70 leading-relaxed">
                {COMPANY.address.street}
                <br />
                {COMPANY.address.zip} {COMPANY.address.city}
              </div>
              <div className="mt-6 relative aspect-[16/9] bg-black/30 border border-white/10 rounded-2xl overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <MapPin size={22} className="text-messinger-orange mx-auto" />
                    <div className="mt-2 text-xs tracking-[0.18em] uppercase text-white/60">
                      Castrop-Rauxel · 44575
                    </div>
                  </div>
                </div>
                <div className="absolute top-3 left-3 text-[10px] tracking-[0.28em] uppercase text-white/30">
                  Standort
                </div>
                <div className="absolute bottom-3 right-3 text-[10px] tracking-[0.28em] uppercase text-white/30">
                  N 51.55° · E 7.31°
                </div>
              </div>
            </div>

            <div className="surface-card rounded-2xl p-8">
              <span className="label-eyebrow">Einzugsgebiet</span>
              <div className="mt-4 flex flex-wrap gap-2">
                {SERVICE_AREA.map((c) => (
                  <span
                    key={c}
                    className="text-xs px-3 py-1.5 bg-white/[0.04] border border-white/10 rounded-full text-white/85"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>
          </motion.aside>
        </div>
      </section>
    </div>
  );
}
